import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/shared/config/env';
import { HTTP_HEADERS, STORAGE_KEYS } from '@/shared/config/constants';
import { storage } from '@/shared/storage/browserStorage';
import { logger } from '@/shared/observability/logger';
import { captureException } from '@/shared/observability/sentry';
import { normaliseAxiosError, ApiError } from './errors';

let onUnauthorizedCallback: (() => void) | null = null;
let onForbiddenCallback: (() => void) | null = null;

/** Allows the auth store / router to subscribe to 401/403 events. */
export function configureHttpAuthHandlers(handlers: {
  onUnauthorized?: () => void;
  onForbidden?: () => void;
}): void {
  if (handlers.onUnauthorized) onUnauthorizedCallback = handlers.onUnauthorized;
  if (handlers.onForbidden) onForbiddenCallback = handlers.onForbidden;
}

function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

function injectAuth(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const token = storage.getString(STORAGE_KEYS.authToken);
  if (token) {
    config.headers.set(HTTP_HEADERS.authorization, `Bearer ${token}`);
  }
  config.headers.set(HTTP_HEADERS.tenantSlug, env.tenantSlug);
  if (!config.headers.has(HTTP_HEADERS.requestId)) {
    config.headers.set(HTTP_HEADERS.requestId, generateRequestId());
  }
  return config;
}

export function createHttpClient(): AxiosInstance {
  const client = axios.create({
    baseURL: env.apiBaseUrl,
    timeout: 30_000,
    withCredentials: false,
  });

  client.interceptors.request.use((config) => {
    const finalConfig = injectAuth(config);
    logger.debug(
      {
        event: 'http.request',
        method: finalConfig.method?.toUpperCase(),
        url: finalConfig.url,
        requestId: finalConfig.headers.get(HTTP_HEADERS.requestId),
      },
      'http request',
    );
    return finalConfig;
  });

  client.interceptors.response.use(
    (response) => {
      logger.debug(
        {
          event: 'http.response',
          status: response.status,
          url: response.config.url,
          requestId: response.config.headers?.get(HTTP_HEADERS.requestId),
        },
        'http response',
      );
      return response;
    },
    (error: AxiosError) => {
      const apiError = normaliseAxiosError(error as AxiosError);
      logger.warn(
        {
          event: 'http.error',
          status: apiError.status,
          code: apiError.code,
          url: error.config?.url,
          requestId: apiError.requestId,
        },
        apiError.message,
      );

      if (apiError.status >= 500 || apiError.status === 0) {
        captureException(apiError, {
          url: error.config?.url,
          method: error.config?.method,
          status: apiError.status,
          requestId: apiError.requestId,
        });
      }

      if (apiError.isUnauthorized) onUnauthorizedCallback?.();
      if (apiError.isForbidden) onForbiddenCallback?.();

      return Promise.reject(apiError);
    },
  );

  return client;
}

export const http: AxiosInstance = createHttpClient();
export type { ApiError };
